import { createPageComposition, getGamesByType } from 'components/visualComposer/utils/utils';
import { useGetMightLikesQuery, useGetPokerQuery, useLazyGetQuickSlotQuery } from 'features/api/apiSlice';
import { PokerCatalogResponseApi, PokerTournamentResponseApi } from 'lib/api/pokerCatalogResponseApi';
import { BANNER_ON_PAGE_PROMO, HERO_BANNER_CMS, HERO_BANNER_PROMO } from 'lib/datoCms/constants';
import React, { useEffect, useMemo } from 'react';
import { isServerSide, LEAVING_LIST, urlLeavingTournaments } from 'utility/constant';

import { BannerHero } from 'components/banner/bannerHero';
import { DefaultCarousel } from 'components/carousels/defaultCarousel';
import { SuggestionCarousel } from 'components/carousels/suggestionCarousel';
import { HeaderTitle } from 'components/headerTitle';
import { LearnMore } from 'components/learnMore';
import { PokerActionBar } from 'components/poker/pokerActionBar';
import { Skeleton } from 'components/skeleton';
import { TabsWithAccordion } from 'components/tabsWithAccordion';
import { VisualComposer } from 'components/visualComposer';
import { useLazyGetPageBannersQuery } from 'features/api/promoEngineApiSlice';
import { useGetMightLikesByList } from 'hooks/useGetAllMightLikes';
import { useGetLabelByKey } from 'hooks/useLingUI';
import { PokerPageQuery } from 'lib/datoCms/queries/getPokerPage';
import { DatoCmsPokerImagesSet } from 'lib/datoCms/types';
import { handleBanner } from 'lib/datoCms/utils';
import { NextPage } from 'next';
import { WithRouterProps } from 'next/dist/client/with-router';
import { withRouter } from 'next/router';
import { conditionsToVisualizeBanner } from 'utility/functions';
import { mergeCmsImageWithPokerCatalogList } from './utils/utils';

type Props = {
  pokerPage: PokerPageQuery['pokerPage'];
  pokerSetImages: DatoCmsPokerImagesSet['allPokerImagesSets'];
} & WithRouterProps;
const PageContent: NextPage<Props> = ({ pokerPage, pokerSetImages }) => {
  /* redux toolkit queries */
  //poker catalog -> TODO : useLazy
  const { isLoading, isError, data: pokerCatalogResposeApi, isUninitialized } = useGetPokerQuery();

  // query promo engine
  const [getPromo, { isError: isErrorPromoEngine, data: promoEngine }] = useLazyGetPageBannersQuery();

  //quickslot -> TODO : useLazy
  const [getQuickSlots, { data: quickSlotResponseApi, isError: isErrorQuickSlot }] = useLazyGetQuickSlotQuery();

  // mightLikes -> TODO : useLazy
  const { data: mightLikesResposeApi } = useGetMightLikesQuery(pokerPage.slug);
  let mightLikesList = useGetMightLikesByList(mightLikesResposeApi?.cardList);

  /* labels */
  const labelLeavingTitle = useGetLabelByKey('in-partenza')!;
  const labelTournamentesLeaving = useGetLabelByKey('tornei-in-partenza')!;
  const apiGenericErrorLabel = useGetLabelByKey('label-api-generic-error');

  const { visualComposer } = pokerPage;

  const listQuickSlot = useMemo(() => {
    if (quickSlotResponseApi) return quickSlotResponseApi.games;
    return;
  }, [quickSlotResponseApi]);

  const pageBannerList = useMemo(() => {
    if (
      conditionsToVisualizeBanner(pokerPage, {
        type: BANNER_ON_PAGE_PROMO,
        data: promoEngine,
        hasError: isErrorPromoEngine,
      })
    ) {
      return promoEngine!.pageBannerList;
    }
    return undefined;
  }, [pokerPage, promoEngine, isErrorPromoEngine]);

  const newVisualComposer = useMemo(() => {
    const newPokerCataloResponseApi = {
      ...pokerCatalogResposeApi!,
      listQuickSlot,
      pageBannerList,
    };

    return (
      visualComposer &&
      newPokerCataloResponseApi &&
      createPageComposition<PokerTournamentResponseApi>({
        visualComposer: visualComposer,
        catalog: newPokerCataloResponseApi,
        slug: pokerPage.slug,
      }).map((e) => e!)
    );
  }, [listQuickSlot, pageBannerList, pokerCatalogResposeApi, pokerPage.slug, visualComposer]);

  // had image for poker tournaments list
  const listLeaving =
    pokerCatalogResposeApi &&
    getGamesByType(pokerCatalogResposeApi! as PokerCatalogResponseApi, LEAVING_LIST as string);

  const newPokerCatalog = mergeCmsImageWithPokerCatalogList(
    pokerSetImages,
    listLeaving as PokerTournamentResponseApi[],
    urlLeavingTournaments
  );

  useEffect(() => {
    if (isServerSide) return;
    getQuickSlots();
  }, [getQuickSlots]);

  useEffect(() => {
    if (isServerSide) return;
    getPromo(String(pokerPage.id));
  }, [pokerPage.id, getPromo]);

  if (isError || isErrorQuickSlot) {
    return <p>{apiGenericErrorLabel}</p>;
  }

  return (
    <React.Fragment>
      {pokerPage.title && <HeaderTitle title={pokerPage.title} />}
      {conditionsToVisualizeBanner(pokerPage, {
        type: HERO_BANNER_PROMO,
        data: promoEngine,
        hasError: isErrorPromoEngine,
      }) && <BannerHero banners={promoEngine!.heroBannerList} />}
      {conditionsToVisualizeBanner(pokerPage, { type: HERO_BANNER_CMS }) && (
        <BannerHero banners={handleBanner(pokerPage.heroBanner[0].banners!)} />
      )}
      <PokerActionBar
        {...pokerPage.pokerPlayLink}
        frameDimension={{
          height: pokerPage.frameHeight,
          width: pokerPage.frameWidth,
        }}
      />
      {!!listLeaving?.length && newPokerCatalog && (
        <DefaultCarousel
          title={labelLeavingTitle}
          subtitle={labelTournamentesLeaving}
          listItem={newPokerCatalog}
          typeOfItem="poker"
        />
      )}

      {isLoading ? (
        <Skeleton hasTitle hasPadding rows={3} />
      ) : (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <React.Fragment>
          {newVisualComposer && <VisualComposer type="giochidicarte" components={newVisualComposer} />}
        </React.Fragment>
      )}

      {!!mightLikesList?.length && <SuggestionCarousel listItem={mightLikesList} />}
      {pokerPage.learnMore && <LearnMore title={pokerPage.learnMore.title} links={pokerPage.learnMore.links} />}
      {pokerPage.tabsWithAccordion && <TabsWithAccordion {...pokerPage.tabsWithAccordion} />}
    </React.Fragment>
  );
};

export default withRouter(PageContent);
